
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { canAddEmailToOrganization } from "@/utils/memberDataIntegrity";
import type { Database } from "@/integrations/supabase/types";

type OrganizationMember = Database['public']['Tables']['organization_members']['Row'];

interface UseMemberOperationsProps {
  organizationId?: string;
  onSuccess?: () => void;
}

export const useMemberOperations = ({ organizationId, onSuccess }: UseMemberOperationsProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);

  const handleAddMember = async (email: string, fullName: string, role: string) => {
    if (!organizationId) {
      toast.error('Organization not found');
      return false;
    }
    
    setIsAdding(true);
    try {
      // Step 1: Check if email can be safely added to this organization
      const validation = await canAddEmailToOrganization(email.toLowerCase(), organizationId);
      
      if (!validation.canAdd) {
        if (validation.reason === 'Email has orphaned record that needs cleanup') {
          toast.error(`Cannot add user: ${validation.reason}. Please contact an administrator to clean up orphaned records.`);
        } else {
          toast.error(`Cannot add user: ${validation.reason}`);
        }
        return false;
      }

      // Step 2: Add member to organization_members table with pending status
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: organizationId,
          email: email.toLowerCase(),
          full_name: fullName,
          role,
          invitation_status: 'pending'
        })
        .select()
        .single();

      if (memberError) throw memberError;
      
      // Step 3: Call the simplified send-invitation edge function
      const { data: functionData, error: functionError } = await supabase.functions
        .invoke("send-invitation", {
          body: { 
            email: email.toLowerCase(), 
            organization_id: organizationId 
          }
        });

      if (functionError) {
        console.error('Edge function error:', functionError);
        toast.error('Failed to send invitation');
        return false;
      }

      const result = functionData;
      console.log('Invitation result:', result);

      if (result?.success && result?.invitation_sent) {
        toast.success('Member added successfully and invitation sent');
      } else {
        toast.warning('Member added but invitation failed to send. You can resend the invitation.');
      }

      onSuccess?.();
      return true;
    } catch (error: any) {
      console.error('Error creating member:', error);
      
      // Handle specific error cases
      if (error.message?.includes('duplicate key') || error.code === '23505') {
        toast.error('This email is already associated with this organization');
      } else {
        toast.error(error.message || 'Failed to create member');
      }
      return false;
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    setUpdatingRole(memberId);
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      onSuccess?.();
      toast.success('Role updated successfully');
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast.error(error.message || 'Failed to update role');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    setRemovingMember(memberId);
    try {
      // Use the new delete-member edge function for complete cleanup
      const { data, error: functionError } = await supabase.functions
        .invoke("delete-member", {
          body: { member_id: memberId }
        });

      if (functionError) {
        console.error('Error deleting member:', functionError);
        throw new Error(functionError.message || 'Failed to delete member');
      }

      console.log('Member deletion result:', data);
      toast.success('Member removed successfully');
      onSuccess?.();
    } catch (error: any) {
      console.error('Error deleting member:', error);
      toast.error(error.message || 'Failed to delete member');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleResendInvitation = async (memberId: string) => {
    setResendingEmail(memberId);
    try {
      // Get member details first
      const { data: member, error: memberError } = await supabase
        .from('organization_members')
        .select('email, organization_id')
        .eq('id', memberId)
        .single();

      if (memberError) throw memberError;

      // Use the dedicated resend-invitation function for resending
      const { data, error: functionError } = await supabase.functions
        .invoke("resend-invitation", {
          body: { 
            member_id: memberId
          }
        });

      if (functionError) {
        console.error('Error resending invitation:', functionError);
        throw new Error(functionError.message || 'Failed to resend invitation');
      }

      const result = data;
      console.log('Invitation resent result:', result);
      
      if (result?.success) {
        toast.success('Invitation email resent successfully');
        onSuccess?.();
      } else {
        toast.error(result?.error || 'Failed to resend invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast.error(error.message || 'Failed to resend invitation');
    } finally {
      setResendingEmail(null);
    }
  };

  return {
    isAdding,
    updatingRole,
    removingMember,
    resendingEmail,
    handleAddMember,
    handleUpdateRole,
    handleDeleteMember,
    handleResendInvitation
  };
};
